import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { AuthenticationService } from '../../data-services/authentication/authentication.service';
import { catchError, delay, mergeMap, retryWhen } from 'rxjs/operators';
import {
  AUTHENTICATION_ERROR,
  MAX_NUMBER_OF_RETRIES,
} from '../../shared/constants';

const getErrorMessage = (maxRetry: number) =>
  `Tried to reach the server ${maxRetry + 1} time(s) without success, giving up. `;

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  refreshSubscription: Subscription;

  constructor(private auth: AuthenticationService) {}

  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler,
  ): Observable<HttpEvent<T>> {
    // Clone the request to add the new header.
    const authReq = req.clone({
      setHeaders: {
        Authorization: this.auth.getAuthorizationHeader(),
        observe: 'response' as const,
      },
    });
    // Pass on the cloned request instead of the original request.
    return next.handle(authReq).pipe(
      this.delayedRetry(500, MAX_NUMBER_OF_RETRIES, 1000),
      catchError((error) => {
        console.error('Error: ', error);
        return throwError(error);
      }),
    );
  }

  delayedRetry<T>(delayMs: number, maxRetry: number, backoffMs: number) {
    let retries = maxRetry;
    return (src: Observable<T>) =>
      src.pipe(
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              if (error && error.status === AUTHENTICATION_ERROR) {
                retries = 0;
                return throwError(error);
              } else if (retries-- > 0) {
                const backoffTime = delayMs + (maxRetry - retries) * backoffMs;
                return of(error).pipe(delay(backoffTime));
              }
              const errorMessage =
                'Error:' +
                JSON.stringify(error, (key, val) => {
                  if (typeof val === 'function') {
                    return String(val);
                  }
                  return val;
                });
              return throwError({
                message: getErrorMessage(maxRetry) + errorMessage,
                error: error,
              });
            }),
          ),
        ),
      );
  }

  // shutdownCurrentSubscription() {
  //   if (this.refreshSubscription && !this.refreshSubscription.closed) {
  //     this.refreshSubscription.unsubscribe();
  //   }
  // }
}
